/**
 * Shop-System (docs/02, SCOPE_STAGE2 Punkt 4): binaere Pfad-Wahl, Locks, zwei
 * Reroll-Typen mit gemeinsamem Kostenzaehler, Cross-Boni, Free Rerolls zuerst.
 */
import type { RunState, ShopPath, ShopSlot, Tier } from './types'
import { drawBonusChampKey, drawChampionSlot, drawPassiveSlot, drawWeaponSlot } from './draws'
import { isLowHp, rerollCost } from './economy'
import { benchFull, storageFull } from './roster'

/** Shop-Eintritt: Free Rerolls einsammeln (Recovery + Low-HP + passende Cross-Boni) */
export function enterShop(state: RunState, visit: 1 | 2): void {
  const cfg = state.config
  let free = state.recoveryTokens
  state.recoveryTokens = 0
  // Low HP Mode: "+1 Free Reroll/Runde", bewertet ZU SHOP-BEGINN (docs/01) —
  // max 1x pro Runde, aber auch der 2. Shop qualifiziert, wenn Shop 1 noch >= 30 HP hatte
  if (isLowHp(state) && state.lowHpRerollRound !== state.round) {
    free += cfg.economy.lowHpFreeRerolls
    state.lowHpRerollRound = state.round
  }
  // A-CC36: ungenutzte Free Rerolls ueberleben INNERHALB der Runde (Shop 1 -> Shop 2),
  // verfallen am Rundenende — "pro Runde"-Ressourcen (Low-HP-Reroll, docs/01) bleiben
  // damit die ganze Runde nutzbar
  if (visit === 2) free += state.shop.freeRerolls
  // A-CC34: gelockte, ungekaufte Slots ueberleben den Shop-Wechsel (Angebot inkl.
  // Preis eingefroren); eingeloest werden sie erst bei passender Pfadwahl (fillSlots)
  const carried = state.shop.slots.filter((sl) => sl.locked && !sl.bought)
  state.shop = {
    visit,
    path: null,
    slots: carried,
    paidRerollsUsed: 0,
    freeRerolls: free,
    weaponRatio: cfg.shop.itemWeaponsDefault,
    repairsUsed: 0,
  }
  state.phase = 'shop'
}

/** S2 v1.2: Item-Pfad braucht >=1 Champion im Besitz (UX-Guard; Bodennetz = Auto-Loss) */
export function canChooseItemPath(state: RunState): boolean {
  return state.champions.length > 0
}

/** binaere Wahl (einmal pro Shop-Besuch); generiert das Angebot */
export function choosePath(state: RunState, path: ShopPath): boolean {
  if (state.phase !== 'shop' || state.shop.path !== null) return false
  if (path === 'item' && !canChooseItemPath(state)) return false // S2-Guard
  state.shop.path = path
  // A-CC34-Nachtrag: gecarriete Locks definieren die Mindest-Ratio — der Default-Reset
  // von weaponRatio darf keine passenden Locks fressen (Locks > Default-Slots-Fall)
  if (path === 'item') {
    const lockedW = state.shop.slots.filter((sl) => sl.locked && !sl.bought && sl.kind === 'weapon').length
    const lockedP = state.shop.slots.filter((sl) => sl.locked && !sl.bought && sl.kind === 'passive').length
    const total = state.config.shop.itemSlotsTotal
    state.shop.weaponRatio = Math.max(lockedW, Math.min(total - lockedP, state.shop.weaponRatio))
  }
  // S1 v1.2: Bonus-Charakter fix pro RUNDE — ein Draw beim ersten Item-Eintritt der Runde
  if (path === 'item' && (state.bonusChamp === null || state.bonusChamp.round !== state.round)) {
    state.bonusChamp = { key: drawBonusChampKey(state), round: state.round, taken: false }
  }
  // Cross-Boni des passenden Typs einloesen — nur aus FRUEHEREN Runden ("kuenftige Runde", docs/02)
  const redeemable = (b: { kind: string; grantedRound: number; expiresAfterRound: number }): boolean =>
    b.kind === path && b.grantedRound < state.round && b.expiresAfterRound >= state.round
  const matching = state.crossBonuses.filter(redeemable)
  for (const b of matching) state.shop.freeRerolls += b.amount
  state.crossBonuses = state.crossBonuses.filter((b) => !redeemable(b))
  fillSlots(state)
  return true
}

function drawSlotFor(state: RunState, kind: 'champion' | 'weapon' | 'passive'): ShopSlot {
  if (kind === 'champion') return drawChampionSlot(state)
  if (kind === 'weapon') return drawWeaponSlot(state)
  return drawPassiveSlot(state)
}

/**
 * Slots (neu) befuellen. Gelockte Slots ueberleben Reroll, Ratio-Shift UND
 * Shop-Wechsel bei passendem Pfad (A-CC34) — per Reassignment auf den ersten
 * passenden Slot-Typ, nicht index-gebunden (die W/P-Grenze wandert beim Ratio-Shift).
 * Locks ohne passenden Slot-Typ (z.B. Champion-Lock im Item-Pfad) verfallen.
 * Gekaufte Slots werden IMMER neu befuellt (docs/02 Option A).
 */
function fillSlots(state: RunState): void {
  const cfg = state.config
  const shop = state.shop
  const desired: Array<'champion' | 'weapon' | 'passive'> = []
  if (shop.path === 'character') {
    for (let i = 0; i < cfg.shop.charOfferCount; i++) desired.push('champion')
  } else {
    const weapons = Math.max(0, Math.min(cfg.shop.itemSlotsTotal, shop.weaponRatio))
    for (let i = 0; i < weapons; i++) desired.push('weapon')
    for (let i = weapons; i < cfg.shop.itemSlotsTotal; i++) desired.push('passive')
    // Bonus-Charakter lebt NICHT in den Slots (S1 v1.2: fix pro Runde, kein Reroll-Einfluss)
  }
  // Pool gelockter, ungekaufter Slots (Reihenfolge = bisherige Slot-Reihenfolge, deterministisch)
  const lockedPool = shop.slots.filter((sl) => sl.locked && !sl.bought)
  // S3b Dup-Kappe (Flag): zaehlt NUR Slots des aktuell konstruierten Boards inkl. Locks;
  // gekaufte Slots und Besitz zaehlen NICHT (A-CC30). Exclusion-Draw statt Rejection-Loop.
  const capOn = cfg.shop.dupCapEnabled > 0
  const counts = new Map<string, number>()
  const atCap = (): Set<string> => {
    const set = new Set<string>()
    if (!capOn) return set
    for (const [k, n] of counts) {
      if (n >= cfg.shop.dupCapPerBoard) set.add(k)
    }
    return set
  }
  shop.slots = desired.map((kind) => {
    const idx = lockedPool.findIndex((sl) => sl.kind === kind)
    let slot: ShopSlot
    if (idx >= 0) {
      slot = lockedPool.splice(idx, 1)[0]!
    } else if (kind === 'champion') {
      slot = drawChampionSlot(state, { exclude: atCap() })
    } else {
      slot = drawSlotFor(state, kind)
    }
    if (slot.kind === 'champion') counts.set(slot.key, (counts.get(slot.key) ?? 0) + 1)
    return slot
  })
  // Locks, fuer die kein passender Slot-Typ mehr existiert (z.B. Waffe bei Ratio 0W),
  // verfallen zwangslaeufig — einziger Fall, in dem ein Lock verloren geht.
}

export function currentRerollCost(state: RunState): number {
  return rerollCost(state.shop.paidRerollsUsed, state.round, state.config)
}

/** beide Reroll-Typen, gemeinsamer Zaehler; Free Rerolls zuerst, erhoehen Zaehler nicht */
export function reroll(state: RunState, kind: 'board' | 'ratioWeapons' | 'ratioPassives'): boolean {
  const shop = state.shop
  if (state.phase !== 'shop' || shop.path === null) return false
  if (kind !== 'board' && shop.path !== 'item') return false
  if (kind === 'ratioWeapons' && shop.weaponRatio >= state.config.shop.itemSlotsTotal) return false
  if (kind === 'ratioPassives' && shop.weaponRatio <= 0) return false
  // "Ratio Shift: ... Locks bleiben" (docs/02): Shift ablehnen, wenn er gelockte
  // Slots des schrumpfenden Typs verdraengen wuerde
  const lockedOf = (k: 'weapon' | 'passive'): number =>
    shop.slots.filter((sl) => sl.locked && !sl.bought && sl.kind === k).length
  if (kind === 'ratioWeapons' && state.config.shop.itemSlotsTotal - (shop.weaponRatio + 1) < lockedOf('passive')) return false
  if (kind === 'ratioPassives' && shop.weaponRatio - 1 < lockedOf('weapon')) return false

  if (shop.freeRerolls > 0) {
    shop.freeRerolls--
  } else {
    const cost = currentRerollCost(state)
    if (state.gold < cost) return false
    state.gold -= cost
    shop.paidRerollsUsed++
  }
  if (kind === 'ratioWeapons') shop.weaponRatio++
  if (kind === 'ratioPassives') shop.weaponRatio--
  fillSlots(state) // Locks ueberleben beide Reroll-Typen (docs/02)
  return true
}

export function toggleLock(state: RunState, slotIdx: number): boolean {
  const slot = state.shop.slots[slotIdx]
  if (!slot || slot.bought) return false
  slot.locked = !slot.locked // Lock gratis (docs/02)
  return true
}

export function buySlot(state: RunState, slotIdx: number): boolean {
  const slot = state.shop.slots[slotIdx]
  if (state.phase !== 'shop' || !slot || slot.bought) return false
  if (state.gold < slot.price) return false
  if (slot.kind === 'champion') {
    if (benchFull(state)) return false
    state.champions.push({
      uid: state.nextUid++,
      champ: slot.key,
      tier: slot.tier,
      boughtRound: state.round,
      buyPrice: slot.price,
      weaponUids: [],
      hex: null,
    })
  } else if (slot.kind === 'weapon') {
    if (storageFull(state)) return false
    state.weapons.push({
      uid: state.nextUid++,
      weapon: slot.key,
      tier: slot.tier as Tier,
      boughtRound: state.round,
      buyPrice: slot.price,
    })
  } else {
    // Passives wirken sofort, keine Lagerung (docs/02) — teamweit permanent (A-CC16)
    state.passives.push(slot.key)
  }
  state.gold -= slot.price
  slot.bought = true
  slot.locked = false
  return true
}

/**
 * S1 v1.2: Bonus-Charakter GRATIS nehmen. Max 1x pro Runde (taken-Flag),
 * kein Slot-Refill (er lebt nicht in den Slots), buyPrice=0 (Verkaufswert 0).
 * Bench voll -> nicht nehmbar (zaehlt als Ablehnung, A-CC29).
 */
export function takeBonusChamp(state: RunState): boolean {
  if (state.phase !== 'shop' || state.shop.path !== 'item') return false
  const b = state.bonusChamp
  if (!b || b.taken || b.round !== state.round) return false
  if (benchFull(state)) return false
  state.champions.push({
    uid: state.nextUid++,
    champ: b.key,
    tier: 1,
    boughtRound: state.round,
    buyPrice: 0,
    weaponUids: [],
    hex: null,
  })
  b.taken = true
  // "bei Ablehnung" (docs/02): nachtraegliche Nahme im 2. Shop derselben Runde nimmt
  // den bereits vergebenen Ablehnungs-Bonus zurueck — kein Doppel-Dip (Champ + Bonus)
  state.crossBonuses = state.crossBonuses.filter(
    (bonus) => !(bonus.kind === 'character' && bonus.grantedRound === state.round),
  )
  return true
}

/**
 * Shop verlassen (Confirm). Setzt Cross-Boni:
 * - Character-Pfad gewaehlt -> +2 Free Item-Rerolls (2 Runden, kein Stapeln)
 * - Item-Pfad: Bonus-Champ NICHT gekauft -> +1 Free Character-Reroll (2 Runden)
 */
export function confirmShop(state: RunState): void {
  const cfg = state.config
  const shop = state.shop
  if (shop.path === 'character') {
    addCrossBonus(state, 'item', cfg.shop.crossBonusItemRerolls, cfg.shop.crossBonusDurationRounds)
  } else if (shop.path === 'item') {
    // "bei Ablehnung" (docs/02 v1.1): Bonus-Charakter dieser Runde nicht genommen
    // (auch: Bench voll = Ablehnung, A-CC29)
    const b = state.bonusChamp
    if (b && b.round === state.round && !b.taken) {
      addCrossBonus(state, 'character', cfg.shop.crossBonusCharRerolls, cfg.shop.crossBonusDurationRounds)
    }
  }
}

function addCrossBonus(state: RunState, kind: 'item' | 'character', amount: number, duration: number): void {
  // kein Stapeln (docs/02): existierender Bonus gleichen Typs wird ersetzt, nicht addiert
  state.crossBonuses = state.crossBonuses.filter((b) => b.kind !== kind)
  state.crossBonuses.push({ kind, amount, grantedRound: state.round, expiresAfterRound: state.round + duration })
}

/** verfallene Cross-Boni aufraeumen (bei Rundenwechsel) */
export function expireCrossBonuses(state: RunState): void {
  state.crossBonuses = state.crossBonuses.filter((b) => b.expiresAfterRound >= state.round)
}
